<template>
  <a-card
    id="value-story-container"
    :body-style="{ padding: '16px' }"
    :bordered="false"
  >
    <template #title>
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h6 class="m-0"><strong>Value Story</strong></h6>
          <small class="text-secondary">
            Configure ROI assumptions for <strong>{{ taskName }}</strong>
          </small>
        </div>
        <a-tag v-if="isDirty" color="orange">Unsaved changes</a-tag>
      </div>
    </template>

    <a-tabs
      v-model:activeKey="activeTab"
      type="line"
      class="value-story-tabs"
      @change="handleTabChange"
    >
      <a-tab-pane key="economics" tab="Plant Economics">
        <EconomicsForm
          :economics="draftEconomics"
          @update:economics="handleEconomicsUpdate"
        />
      </a-tab-pane>
      <a-tab-pane key="steps" tab="Failure Modes">
        <StepImportanceTable
          :steps="stepsList"
          :steps-config="draftStepsConfig"
          :economics="draftEconomics"
          @update:stepsConfig="handleStepsConfigUpdate"
        />
      </a-tab-pane>
    </a-tabs>

    <div class="footer-row">
      <div>
        <a-button
          v-if="activeTab === 'steps'"
          :disabled="isUpdatingValueStoryConfig"
          @click="handleBack"
        >
          <template #icon><arrow-left-outlined /></template>
          Back
        </a-button>
      </div>
      <a-space>
        <a-button
          :disabled="isUpdatingValueStoryConfig"
          @click="handleCancel"
        >
          Cancel
        </a-button>
        <a-button
          v-if="activeTab === 'economics'"
          type="primary"
          :loading="isUpdatingValueStoryConfig"
          @click="handleNext"
        >
          Next
          <template #icon><arrow-right-outlined /></template>
        </a-button>
        <a-button
          v-else
          type="primary"
          :loading="isUpdatingValueStoryConfig"
          @click="handleSaveAndClose"
        >
          Save &amp; Close
        </a-button>
      </a-space>
    </div>
  </a-card>
</template>

<script>
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons-vue';
import { DEFAULT_ECONOMICS } from 'src/utils/valueStory/config';
import { mapActions, mapGetters } from 'vuex';
import EconomicsForm from './EconomicsForm.vue';
import StepImportanceTable from './StepImportanceTable.vue';

function cloneConfig(cfg) {
  return {
    economics: { ...DEFAULT_ECONOMICS, ...((cfg && cfg.economics) || {}) },
    steps: JSON.parse(JSON.stringify((cfg && cfg.steps) || {})),
  };
}

export default {
  components: {
    EconomicsForm,
    StepImportanceTable,
    ArrowLeftOutlined,
    ArrowRightOutlined,
  },
  inject: ['toast'],
  data() {
    return {
      activeTab: 'economics',
      draftEconomics: { ...DEFAULT_ECONOMICS },
      draftStepsConfig: {},
      savedEconomicsSnapshot: null,
      savedStepsSnapshot: null,
      pendingTab: null,
      confirmingDiscard: false,
    };
  },
  computed: {
    ...mapGetters([
      'taskName',
      'taskProcesses',
      'valueStoryConfig',
      'isUpdatingValueStoryConfig',
    ]),
    stepsList() {
      const list = [];
      let index = 0;
      for (const process of this.taskProcesses || []) {
        for (const step of process.steps || []) {
          for (const substep of step.substeps || []) {
            list.push({
              index,
              name: substep,
            });
            index += 1;
          }
        }
      }
      return list;
    },
    isDirty() {
      return (
        JSON.stringify(this.draftEconomics) !==
          JSON.stringify(this.savedEconomicsSnapshot) ||
        JSON.stringify(this.draftStepsConfig) !==
          JSON.stringify(this.savedStepsSnapshot)
      );
    },
  },
  mounted() {
    const cloned = cloneConfig(this.valueStoryConfig);
    this.draftEconomics = cloned.economics;
    this.draftStepsConfig = cloned.steps;
    this.savedEconomicsSnapshot = JSON.parse(
      JSON.stringify(this.draftEconomics)
    );
    this.savedStepsSnapshot = JSON.parse(
      JSON.stringify(this.draftStepsConfig)
    );
  },
  methods: {
    ...mapActions([
      'updateValueStoryModalState',
      'updateValueStoryConfig',
    ]),
    handleEconomicsUpdate(next) {
      this.draftEconomics = next;
    },
    handleStepsConfigUpdate(next) {
      this.draftStepsConfig = next;
    },
    handleTabChange(nextKey) {
      // Allow free-navigation but let the parent modal's close-confirmation
      // guard handle unsaved edits when the user closes.
      this.activeTab = nextKey;
    },
    async handleNext() {
      const result = await this.updateValueStoryConfig({
        economics: this.draftEconomics,
        steps: this.draftStepsConfig,
      });
      if (result?.error) {
        this.toast.error('Failed to save plant economics.');
        return;
      }
      this.savedEconomicsSnapshot = JSON.parse(
        JSON.stringify(this.draftEconomics)
      );
      this.savedStepsSnapshot = JSON.parse(
        JSON.stringify(this.draftStepsConfig)
      );
      this.toast.success('Plant economics saved.');
      this.activeTab = 'steps';
    },
    handleBack() {
      this.activeTab = 'economics';
    },
    async handleSaveAndClose() {
      const result = await this.updateValueStoryConfig({
        economics: this.draftEconomics,
        steps: this.draftStepsConfig,
      });
      if (result?.error) {
        this.toast.error('Failed to save value story.');
        return;
      }
      this.toast.success('Value story saved.');
      this.updateValueStoryModalState(false);
    },
    handleCancel() {
      this.updateValueStoryModalState(false);
    },
  },
};
</script>

<style scoped>
.value-story-tabs :deep(.ant-tabs-content) {
  min-height: 55vh;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px;
}
#value-story-container {
  overflow-x: hidden;
}
.footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
}
</style>
